import { Component, inject, OnInit, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EmpresaService } from '../../services/empresa.service';
import { Empresa } from '../../models/empresa.model';
import { Subject } from 'rxjs';
import { ModalCrearComponent } from './modal-crear/modal-crear.component';
import { ModalEditarComponent } from './modal-editar/modal-editar.component';
import { ModalEliminarComponent } from './modal-eliminar/modal-eliminar.component';
import { ModalSunatComponent } from './modal-sunat/modal-sunat.component';
import { ToastService } from '../../services/toast.service';
import { ToastComponent } from '../../components/toast/toast.component';
import { takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { ModalService, ModalOptions } from '../../services/modal.service';
import { AuthService } from '../../services/auth.service';
import {
  SunatReconsultaError,
  SunatSyncSummary,
} from '../../models/sunat.model';

@Component({
  selector: 'app-empresas',
  imports: [
    CommonModule,
    FormsModule,
    ModalCrearComponent,
    ModalEditarComponent,
    ModalEliminarComponent,
    ModalSunatComponent,
    ReactiveFormsModule,
    ToastComponent,
  ],
  templateUrl: './empresas.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './empresas.component.css',
})
export class EmpresasComponent implements OnInit {
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private router = inject(Router);

  private destroy$ = new Subject<void>();
  // Propiedades principales

  empresaSeleccionada: Empresa | null = null;

  empresas: Empresa[] = [];
  filteredEmpresas: Empresa[] = [];
  paginatedEmpresas: Empresa[] = [];

  // Propiedades de control
  loading = false;
  error = '';

  // Propiedades de búsqueda y filtrado
  searchTerm: string = '';
  selectedFilter: string = 'Todas';

  // Propiedades de paginación
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;

  private empresaService = inject(EmpresaService);

  ngOnInit(): void {
    this.loadEmpresas();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga todas las empresas desde el backend
   */
  loadEmpresas(): void {
    this.loading = true;
    this.error = '';

    // Usar servicio real
    this.empresaService
      .getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresas) => {
          this.empresas = empresas;
          this.filteredEmpresas = [...this.empresas];
          console.log('Empresas cargadas:', this.empresas);
          this.updatePagination();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar empresas:', error);
          this.error =
            'Error al cargar las empresas. Por favor, intente nuevamente.';
          this.loading = false;
          this.filteredEmpresas = [...this.empresas];
          this.updatePagination();
        },
      });
  }

  getEmpresasTotal(): number {
    return this.empresas.length;
  }

  refreshData(): void {
    this.loadEmpresas();
  }

  // Funciones de búsqueda y filtrado
  onSearchChange(): void {
    this.filterEmpresas();
  }

  filterEmpresas(): void {
    let filtered = [...this.empresas];

    // Aplicar filtro de búsqueda
    if (this.searchTerm.trim()) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (empresa) =>
          empresa.ruc.toLowerCase().includes(search) ||
          empresa.nombre.toLowerCase().includes(search),
      );
    }

    // Aplicar filtro de estado
    const status = this.selectedFilter;
    console.log('Aplicando filtro de estado:', status);
    if (status !== 'Todas') {
      filtered = filtered.filter((empresa) => {
        if (status === 'Activa') {
          return (
            empresa.licencia &&
            new Date(empresa.licencia.fecha_fin) > new Date()
          );
        }
        if (status === 'Inactiva') {
          return (
            empresa.licencia &&
            new Date(empresa.licencia.fecha_fin) <= new Date()
          );
        }
        return true;
      });
    }

    this.filteredEmpresas = filtered;
    this.currentPage = 1;
    this.updatePagination();
  }

  // Funciones de paginación
  updatePagination(): void {
    this.totalPages = Math.ceil(
      this.filteredEmpresas.length / this.itemsPerPage,
    );
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedEmpresas = this.filteredEmpresas.slice(startIndex, endIndex);
  }

  getStartIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getEndIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return end > this.filteredEmpresas.length
      ? this.filteredEmpresas.length
      : end;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);

    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  editarEmpresa(empresa: Empresa): void {
    console.log('Editar empresa:', empresa);
  }

  openModal(
    modal: TemplateRef<unknown>,
    empresa: Empresa | null = null,
    options?: ModalOptions,
  ) {
    this.empresaSeleccionada = empresa;
    this.modalService.open(modal, options);

    console.log('Empresa seleccionada: ', this.empresaSeleccionada);

    console.log('Datos enviado al modal!!!');
  }

  irASedes(empresa: Empresa): void {
    this.router.navigate(['/dashboard/empresas', empresa.id, 'sedes']);
  }

  irAUsuarios(empresa: Empresa): void {
    this.router.navigate(['/dashboard/empresas', empresa.id, 'usuarios']);
  }

  onDeleteSuccess() {
    this.toastService.show({
      text: 'Empresa eliminada exitosamente',
      classname: 'bg-success text-light',
      delay: 3000,
    });
    this.loadEmpresas();
  }

  onDeleteError(errorMessage: string) {
    this.toastService.show({
      text: errorMessage,
      classname: 'bg-danger text-light',
      delay: 5000,
    });
  }

  onCreateSuccess() {
    this.toastService.show({
      text: 'Empresa creada exitosamente',
      classname: 'bg-success text-light',
      delay: 3000,
    });
    this.loadEmpresas();
  }

  onEditSuccess() {
    this.toastService.show({
      text: 'Empresa actualizada exitosamente',
      classname: 'bg-success text-light',
      delay: 3000,
    });
    this.loadEmpresas();
  }

  onEditError(errorMessage: string) {
    this.toastService.show({
      text: errorMessage,
      classname: 'bg-danger text-light',
      delay: 5000,
    });
  }

  onCreateError(errorMessage: string) {
    this.toastService.show({
      text: errorMessage,
      classname: 'bg-danger text-light',
      delay: 5000,
    });
  }

  private get ejecutadoPor(): string {
    return this.authService.getCurrentUser()?.email ?? 'usuario actual';
  }

  onReconsultaSunatExitosa(resumen: SunatSyncSummary) {
    const detalle =
      `${resumen.procesadas} procesadas, ${resumen.actualizadas} actualizadas, ` +
      `${resumen.sin_cambios} sin cambios, ${resumen.sin_token} sin token, ` +
      `${resumen.errores} errores`;

    const aviso = resumen.en_curso
      ? ' (ya había una reconsulta en curso)'
      : '';

    this.toastService.show({
      text: `Reconsulta SUNAT ejecutada por ${this.ejecutadoPor}: ${detalle}${aviso}`,
      classname:
        resumen.errores > 0 || resumen.en_curso
          ? 'bg-warning text-light'
          : 'bg-success text-light',
      delay: 7000,
    });
  }

  onReconsultaSunatError(error: SunatReconsultaError) {
    const codigo = error.status > 0 ? ` (HTTP ${error.status})` : '';
    this.toastService.show({
      text: `Reconsulta SUNAT falló${codigo}: ${error.message}`,
      classname: 'bg-danger text-light',
      delay: 9000,
    });
  }
}
